import { supabase } from '../lib/supabase';
import { useOrganization } from '../contexts/OrganizationContext';
import { notifyTaskAssigned } from './notificationService';

export interface SimpleTask {
  id: string;
  title: string;
  description: string | null;
  completed: boolean;
  due_date: string | null;
  user_id: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  created_by_name?: string;
}

export interface CreateSimpleTaskData {
  title: string;
  description?: string;
  due_date?: string | null;
}

export const simpleTaskService = {
  // Buscar todas as tarefas do usuário na organização
  async getAllTasks(organizationId: string, userId: string): Promise<SimpleTask[]> {
    const { data, error } = await supabase
      .from('simple_tasks')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching tasks:', error);
      throw error;
    }

    return data || [];
  },

  // Buscar tarefas sem data (Entrada) do usuário
  async getInboxTasks(organizationId: string, userId: string): Promise<SimpleTask[]> {
    const { data, error } = await supabase
      .from('simple_tasks')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('user_id', userId)
      .is('due_date', null)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar tarefas da Entrada:', error);
      throw error;
    }

    return data || [];
  },

  // Buscar tarefas para hoje - CORRIGIDO
  async getTodayTasks(organizationId: string, userId: string): Promise<SimpleTask[]> {
    try {
      console.log('[simpleTaskService] Iniciando busca de tarefas para hoje');
      
      // Primeiro, vamos buscar todas as tarefas do usuário para debug
      const { data: allTasks, error: allTasksError } = await supabase
        .from('simple_tasks')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
        
      console.log('[simpleTaskService] TODAS as tarefas do usuário:', allTasks);
      
      // Obter a data de hoje no fuso horário local
      const today = new Date();
      
      // Criar datas de início e fim do dia no fuso horário local
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
      
      // Converter para ISO string
      const startOfDayISO = startOfDay.toISOString();
      const endOfDayISO = endOfDay.toISOString();
      
      console.log('[simpleTaskService] Buscando tarefas para hoje:', {
        data: today.toLocaleDateString(),
        startOfDay: startOfDayISO,
        endOfDay: endOfDayISO,
        organizationId,
        userId
      });
      
      // Usar between para capturar todas as tarefas do dia
      const { data, error } = await supabase
        .from('simple_tasks')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('user_id', userId)
        .gte('due_date', startOfDayISO)
        .lte('due_date', endOfDayISO)
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error('[simpleTaskService] Erro na consulta:', error);
        throw error;
      }
      
      console.log('[simpleTaskService] Tarefas encontradas para hoje:', data);
      console.log('[simpleTaskService] Comparação manual das datas:');
      if (allTasks) {
        allTasks.forEach(task => {
          if (task.due_date) {
            const taskDate = new Date(task.due_date);
            const isToday = taskDate >= startOfDay && taskDate <= endOfDay;
            console.log(`- Tarefa "${task.title}": ${task.due_date} -> É hoje? ${isToday}`);
          }
        });
      }
      
      return data || [];
    } catch (err) {
      console.error('[simpleTaskService] Erro geral:', err);
      throw err;
    }
  },

  // Buscar tarefas futuras - CORRIGIDO
  async getUpcomingTasks(organizationId: string, userId: string): Promise<SimpleTask[]> {
    try {
      // Obter a data de hoje
      const today = new Date();
      
      // Normalizar a data de hoje (remover horas, minutos, segundos)
      const todayNormalized = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );
      
      // Criar uma nova data para amanhã
      const tomorrow = new Date(todayNormalized);
      tomorrow.setDate(todayNormalized.getDate() + 1);
      
      // Converter para ISO string
      const tomorrowISO = tomorrow.toISOString();
      
      console.log('[simpleTaskService] Buscando tarefas futuras:', {
        hoje: todayNormalized.toLocaleDateString(),
        amanha: tomorrow.toLocaleDateString(),
        amanhaISO: tomorrowISO
      });
      
      const { data, error } = await supabase
        .from('simple_tasks')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('user_id', userId)
        .gte('due_date', tomorrowISO)
        .order('due_date', { ascending: true });

      if (error) {
        console.error('[simpleTaskService] Erro ao buscar tarefas futuras:', error);
        throw error;
      }

      console.log('[simpleTaskService] Tarefas futuras encontradas:', data?.length || 0);
      return data || [];
    } catch (err) {
      console.error('[simpleTaskService] Erro geral:', err);
      throw err;
    }
  },

  // Buscar tarefas atrasadas (com data anterior a hoje)
  async getOverdueTasks(organizationId: string): Promise<SimpleTask[]> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const { data, error } = await supabase
      .from('simple_tasks')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('completed', false)
      .lt('due_date', today.toISOString())
      .order('due_date', { ascending: true });

    if (error) {
      console.error('Error fetching overdue tasks:', error);
      throw error;
    }

    return data || [];
  },

  // Criar uma nova tarefa
  async createTask(task: Omit<SimpleTask, 'id' | 'created_at' | 'updated_at'>): Promise<SimpleTask | null> {
    console.log('[simpleTaskService] Enviando para insert:', task);
    const { data, error } = await supabase
      .from('simple_tasks')
      .insert(task)
      .select()
      .maybeSingle();

    if (error) {
      console.error('Error creating task:', error, 'Payload:', task);
      throw error;
    }

    console.log('[simpleTaskService] Tarefa criada com sucesso:', data);

    // Notificação de tarefa atribuída — só quando é pra outra pessoa. Toda tela
    // de Minhas notas cria a nota com user_id = quem está logado, e notificar
    // aí era a pessoa recebendo "Nova tarefa atribuída" de si mesma.
    const { data: sessao } = await supabase.auth.getSession();
    if (data && data.user_id && data.organization_id && task.user_id !== sessao.session?.user.id) {
      try {
        await notifyTaskAssigned({
          organization_id: data.organization_id,
          user_id: data.user_id,
          taskTitle: data.title,
          projectName: data.project_name || '', // ajuste se necessário
          taskId: data.id,
          projectId: data.project_id || '', // ajuste se necessário
        });
      } catch (notifyError) {
        console.warn('Falha ao notificar usuário sobre tarefa atribuída:', notifyError);
      }
    }

    this.triggerTasksRefresh();
    return data;
  },

  // Atualizar uma tarefa existente
  async updateTask(taskId: string, updates: Partial<SimpleTask>): Promise<SimpleTask | null> {
    const { data, error } = await supabase
      .from('simple_tasks')
      .update(updates)
      .eq('id', taskId)
      .select()
      .single();

    if (error) {
      console.error(`Error updating task ${taskId}:`, error);
      throw error;
    }

    // Disparar evento de atualização para refrescar a interface
    this.triggerTasksRefresh();
    
    return data;
  },

  // Marcar tarefa como completa
  async markTaskAsCompleted(taskId: string): Promise<SimpleTask | null> {
    return this.updateTask(taskId, { completed: true });
  },

  // Marcar tarefa como incompleta
  async markTaskAsIncomplete(taskId: string): Promise<SimpleTask | null> {
    return this.updateTask(taskId, { completed: false });
  },

  // Excluir uma tarefa
  async deleteTask(taskId: string): Promise<void> {
    const { error } = await supabase
      .from('simple_tasks')
      .delete()
      .eq('id', taskId);

    if (error) {
      console.error(`Error deleting task ${taskId}:`, error);
      throw error;
    }
  },
  
  // Método para disparar evento de atualização global
  triggerTasksRefresh(): void {
    console.log('[simpleTaskService] Disparando evento de atualização global');
    window.dispatchEvent(new CustomEvent('tasks-refresh-needed'));
  },
  
  // Mover tarefas não concluídas de hoje para atrasadas (mantendo a data original)
  async moveOverdueTasks(organizationId: string): Promise<number> {
    try {
      // Obter a data de hoje no fuso horário local
      const today = new Date();
      
      // Criar datas de início e fim do dia no fuso horário local
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
      
      // Converter para ISO string
      const startOfDayISO = startOfDay.toISOString();
      const endOfDayISO = endOfDay.toISOString();
      
      console.log('[simpleTaskService] Verificando tarefas vencidas de hoje:', {
        data: today.toLocaleDateString(),
        startOfDay: startOfDayISO,
        endOfDay: endOfDayISO
      });
      
      // Buscar tarefas não concluídas programadas para hoje
      const { data, error } = await supabase
        .from('simple_tasks')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('completed', false)
        .gte('due_date', startOfDayISO)
        .lte('due_date', endOfDayISO);
        
      if (error) {
        console.error('[simpleTaskService] Erro ao buscar tarefas vencidas:', error);
        throw error;
      }
      
      const overdueTasks = data || [];
      console.log('[simpleTaskService] Tarefas vencidas encontradas:', overdueTasks.length);
      
      if (overdueTasks.length === 0) {
        return 0;
      }
      
      // Como queremos manter as tarefas no submenu "Atrasadas", mantemos a data original
      // Não precisamos fazer nenhuma alteração nas tarefas, apenas informar que foram processadas

      // Disparar evento de atualização para refletir as alterações na UI
      this.triggerTasksRefresh();
      
      console.log(`[simpleTaskService] ${overdueTasks.length} tarefas permanecerão com suas datas originais e aparecerão no submenu Atrasadas`);
      return overdueTasks.length;
    } catch (err) {
      console.error('[simpleTaskService] Erro ao processar tarefas vencidas:', err);
      throw err;
    }
  },
  
  // Processar tarefas não concluídas de dias anteriores para aparecer em atrasadas
  async moveTasksFromPreviousDays(organizationId: string): Promise<number> {
    try {
      // Obter a data de hoje no fuso horário local
      const today = new Date();
      
      // Criar data de início do dia atual
      const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
      
      // Converter para ISO string
      const startOfTodayISO = startOfToday.toISOString();
      
      console.log('[simpleTaskService] Verificando tarefas de dias anteriores:', {
        dataAtual: today.toLocaleDateString(),
        inicioHoje: startOfTodayISO
      });
      
      // Buscar tarefas não concluídas com datas anteriores a hoje
      const { data, error } = await supabase
        .from('simple_tasks')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('completed', false)
        .lt('due_date', startOfTodayISO);
        
      if (error) {
        console.error('[simpleTaskService] Erro ao buscar tarefas de dias anteriores:', error);
        throw error;
      }
      
      const overdueTasks = data || [];
      console.log('[simpleTaskService] Tarefas de dias anteriores encontradas:', overdueTasks.length);
      
      if (overdueTasks.length === 0) {
        return 0;
      }
      
      // Como queremos manter as tarefas no submenu "Atrasadas", mantemos a data original
      // Não precisamos fazer nenhuma alteração nas tarefas, apenas informar que foram processadas

      // Disparar evento de atualização para refletir as alterações na UI
      this.triggerTasksRefresh();
      
      console.log(`[simpleTaskService] ${overdueTasks.length} tarefas de dias anteriores permanecerão com suas datas originais e aparecerão no submenu Atrasadas`);
      return overdueTasks.length;
    } catch (err) {
      console.error('[simpleTaskService] Erro ao processar tarefas de dias anteriores:', err);
      throw err;
    }
  },

  async getTaskById(taskId: string): Promise<SimpleTask | null> {
    const { data, error } = await supabase
      .from('simple_tasks')
      .select('*')
      .eq('id', taskId)
      .single();

    if (error) {
      console.error(`Error fetching task ${taskId}:`, error);
      return null;
    }

    return data;
  },

  // Buscar tarefas da próxima semana
  async getTasksDueInNextWeek(organizationId: string): Promise<SimpleTask[]> {
    const today = new Date();
    const nextWeek = new Date();
    nextWeek.setDate(today.getDate() + 7);

    const { data, error } = await supabase
      .from('simple_tasks')
      .select('*')
      .eq('organization_id', organizationId)
      .gte('due_date', today.toISOString())
      .lte('due_date', nextWeek.toISOString())
      .order('due_date', { ascending: true });

    if (error) {
      console.error('Error fetching tasks due in next week:', error);
      throw error;
    }

    return data || [];
  },

  // Buscar tarefas completadas em um período
  async getTasksCompletedInTimeRange(organizationId: string, startDate: Date, endDate: Date): Promise<SimpleTask[]> {
    const { data, error } = await supabase
      .from('simple_tasks')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('completed', true)
      .gte('updated_at', startDate.toISOString())
      .lte('updated_at', endDate.toISOString())
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error fetching completed tasks in time range:', error);
      throw error;
    }

    return data || [];
  },

  // Buscar tarefas criadas em um período
  async getTasksCreatedInTimeRange(organizationId: string, startDate: Date, endDate: Date): Promise<SimpleTask[]> {
    const { data, error } = await supabase
      .from('simple_tasks')
      .select('*')
      .eq('organization_id', organizationId)
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString())
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching tasks created in time range:', error);
      throw error;
    }

    return data || [];
  },

  async getTaskCompletionStats(organizationId: string, timeRangeInDays: number = 30): Promise<{
    totalCreated: number,
    totalCompleted: number,
    averageCompletionTimeInDays: number,
    completionRate: number
  }> {
    // Calcular data de início para o intervalo de tempo
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - timeRangeInDays);
    
    try {
      // Buscar tarefas criadas no período
      const tasksCreated = await this.getTasksCreatedInTimeRange(
        organizationId,
        startDate,
        new Date()
      );
      
      // Buscar tarefas concluídas no período
      const tasksCompleted = await this.getTasksCompletedInTimeRange(
        organizationId,
        startDate,
        new Date()
      );
      
      // Calcular taxa de conclusão
      const totalCreated = tasksCreated.length;
      const totalCompleted = tasksCompleted.length;
      const completionRate = totalCreated > 0 ? (totalCompleted / totalCreated) * 100 : 0;
      
      // Calcular tempo médio de conclusão
      let totalCompletionTimeInDays = 0;
      let tasksWithCompletionTime = 0;
      
      tasksCompleted.forEach(task => {
        if (task.created_at) {
          const createdDate = new Date(task.created_at);
          const completedDate = new Date(task.updated_at);
          const diffInMs = completedDate.getTime() - createdDate.getTime();
          const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
          
          totalCompletionTimeInDays += diffInDays;
          tasksWithCompletionTime++;
        }
      });
      
      const averageCompletionTimeInDays = 
        tasksWithCompletionTime > 0 ? totalCompletionTimeInDays / tasksWithCompletionTime : 0;
      
      return {
        totalCreated,
        totalCompleted,
        averageCompletionTimeInDays,
        completionRate
      };
    } catch (error) {
      console.error('Error calculating task completion stats:', error);
      throw error;
    }
  },

  async toggleTaskCompletion(taskId: string, completed: boolean): Promise<SimpleTask | null> {
    const updated = await this.updateTask(taskId, { completed });
    console.log('[simpleTaskService] Tarefa atualizada (toggle):', updated);
    this.triggerTasksRefresh();
    return updated;
  }
};

export default simpleTaskService; 